import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// Components
import Button from "@/main/components/shared/button/button";
import Box from "@/main/components/shared/box";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { AreaButton, KOGAreaButtons } from "@/main/components/shared/styles";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";

// Api's
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getStatusRegister } from "@/main/store/statusRegister";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";
import { getCompany, resetReducerCompany, useCompanyReducer } from "@/main/store/company";

import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { StatusUserGroupEnum } from "@/main/services/enum/StatusUserGroupEnum";
import { usergroupApi } from "@/main/api";

import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";

import { KeyValue } from "@/main/models/common";
import { getOptionsStatus } from "@/main/services/data/data";
import { UserGroupFilterRequest } from "@/main/models/request/usergroupRequest";

import {
	getDefaultOptionDrop,
	getTranslateByKey,
	isValueQueryParams,
	mountReducerWithData,
	removeEmptyValues,
	getDefaultCountryOptionDrop,
	isNullUndefined,
} from "@/main/services/utils";
import { useNavigate } from "react-router-dom";
import { RoutesConstants } from "@/main/routes/routesConstants";
import UsergroupSearchGrid, { IGridData, INITIAL_GRID_STATE } from "./components/userGroupSearchGrid";
import * as S from "./styles";
import { CompanyFilterRequest } from "@/main/models/request";
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import { withQueryparams } from "@/main/components/core/queryParams/queryparams";
import { getSystemApplication, useSystemApplicationReducer } from "@/main/store/systemApplication";
import { userIsInsurer } from "@/main/services/utilsUser";
import { useUserReducer } from "@/main/store/user";

const INITIAL_FORM_STATE = {
	country: null as KeyValue | null,
	companyType: null as KeyValue | null,
	company: null as KeyValue | null,
	userGroup: "" as string,
	statusRegister: null as KeyValue | null,
	active: null as KeyValue | null,
	systemApplication: null as KeyValue | null,
};

type ISearchUserGroupForm = typeof INITIAL_FORM_STATE;

function UsergroupScreen() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// State & reducers
	const countryReducer = useCountryReducer();
	const statusGroupRegister = getOptionsStatus();
	const statusGroupRegisterMounted = mountReducerWithData(statusGroupRegister);
	const companyTypeReducer = useCompanyTypeReducer();
	const companyReducer = useCompanyReducer();
	const systemApplicationReducer = useSystemApplicationReducer();
	const userReducer = useUserReducer();

	const {
		control,
		setValue,
		customHandleSubmit,
		formState: { errors },
		queryParamsValues,
		reset,
		getValues,
	} = useFormQueryParams<ISearchUserGroupForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
		onSubmit: onSubmit,
		mapDefaultValues: {
			company: companyReducer.company,
			statusRegister: statusGroupRegisterMounted,
			companyType: companyTypeReducer.companyType,
			country: countryReducer.countriesUser,
			active: statusGroupRegisterMounted,
			systemApplication: systemApplicationReducer.systemApplication,
		},
	});

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.countriesUser.data);
	const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(companyTypeReducer.companyType.data);

	const optionDefaultCompanyDrop = getDefaultOptionDrop(companyReducer.company.data);

	const [, setHasShowMore] = useState(false);
	const [showFilters, setShowFilters] = useState<boolean>(true);
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [formFilters, setFormFilters] = useState<UserGroupFilterRequest | null>(null);
	const [, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const [, setIsLoadingInfiniteScroll] = useState(false);
	const isInsurerUser = userIsInsurer();

	const isDisabledCompanyTypeDrop =
		companyTypeReducer.companyType.data === null ||
		!companyTypeReducer.companyType.data ||
		companyTypeReducer.companyType.data.length === 1;

	const isDisabledCompanyDrop = () =>
		getValues().companyType === null ||
		!companyReducer.company.data ||
		companyReducer.company.data.length === 1 ||
		companyReducer.company.data.length === 0;

	// UseEffect's
	// Initial load
	useEffect(() => {
		dispatch(getCountriesUser());
		dispatch(getStatusRegister());
		dispatch(
			getCompanyType({
				screenCompanytype: true,
			}),
		);
		dispatch(getSystemApplication());
		return () => {
			dispatch(resetReducerCompany());
		};
	}, []);

	useEffect(() => {
		// Call API's related by default country
		if (optionDefaultCountryDrop && optionDefaultCompanyTypeDrop) {
			callCompanyApiDomain(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
		}
	}, [optionDefaultCountryDrop?.key, optionDefaultCompanyTypeDrop?.key]);

	// Fill and lock filters for Insurer user
	useEffect(() => {
		if (!isInsurerUser) return;

		const defaultCountry = getDefaultCountryOptionDrop(countryReducer.countriesUser.data);
		if (defaultCountry) {
			setValue("country", defaultCountry);
		}
	}, [isInsurerUser, countryReducer.countriesUser.data, companyTypeReducer.companyType.data]);

	useEffect(() => {
		if (!isInsurerUser) return;
		if (companyReducer.company.data && companyReducer.company.data.length > 0) {
			const defaultCompany = getDefaultOptionDrop(companyReducer.company.data);

			if (defaultCompany) {
				setValue("company", defaultCompany);
			}
		}
	}, [isInsurerUser, companyReducer.company.data]);

	function handleNew() {
		dispatch(resetReducerCompany());
		return navigate(`${RoutesConstants.registrationRegisterUsergroup}`);
	}
	function handleFilters() {
		setShowFilters(!showFilters);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}

	function handleClear() {
		reset(INITIAL_FORM_STATE);

		if (isInsurerUser) {
			const defaultCountry = getDefaultCountryOptionDrop(countryReducer.countriesUser.data);
			if (defaultCountry) setValue("country", defaultCountry);

			if (companyTypeReducer.companyType.data !== null && companyTypeReducer.companyType.data.length === 1) {
				const keyCompanyTypeInsurer = String(CompanyTypeEnum.Insurer);
				const valueCompanyTypeInsurer = String(
					companyTypeReducer.companyType.data?.find((x) => x.key === keyCompanyTypeInsurer)?.value || "",
				);
				setValue("companyType", {
					key: keyCompanyTypeInsurer,
					value: valueCompanyTypeInsurer,
				} as any);
				if (defaultCountry !== null) {
					callCompanyApiDomain(defaultCountry, {
						key: keyCompanyTypeInsurer,
						value: valueCompanyTypeInsurer,
					} as any);
				}
			}

			setValue("userGroup", "");
			setFormFilters(null);
			setGridData(INITIAL_GRID_STATE);
			setPageIndex(PaginationEnum.PageIndexIntial);
			setHasShowMore(false);
			setIsLoadingInfiniteScroll(false);
		} else {
			if (companyTypeReducer.companyType.data !== null && companyTypeReducer.companyType.data.length === 1) {
				const keyCompanyType = String(CompanyTypeEnum.Admin);
				const valueCompanyType = String(
					companyTypeReducer.companyType.data?.find((x) => x.key === keyCompanyType)?.value,
				);
				setValue("companyType", {
					key: keyCompanyType,
					value: valueCompanyType,
				});
			}
			setValue("country", optionDefaultCountryDrop);
			setValue("companyType", optionDefaultCompanyTypeDrop);
			setValue("userGroup", "");
			setFormFilters(null);
			setGridData(INITIAL_GRID_STATE);
			setPageIndex(PaginationEnum.PageIndexIntial);
			setHasShowMore(false);
			setIsLoadingInfiniteScroll(false);

			if (optionDefaultCountryDrop !== null && optionDefaultCompanyTypeDrop !== null) {
				callCompanyApiDomain(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
			}
		}
	}

	function callCompanyApiDomain(countrySelected: KeyValue | null, companyTypeSelected: KeyValue | null) {
		if (!isValueQueryParams(countrySelected) || !isValueQueryParams(companyTypeSelected)) {
			setValue("company", null);
		}

		dispatch(resetReducerCompany());

		if (countrySelected !== null) {
			const request: CompanyFilterRequest = {
				guidCountry: String(countrySelected.key),
				type: HeadQuarterBranchFilterEnum.All,
				seeOnlyCompaniesWithAnAgreement: companyTypeSelected?.key == CompanyTypeEnum.Insurer ? false : true,
				guidCompany:
					userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer
						? userReducer.currentUser?.guidCompany
						: null,
			};
			if (companyTypeSelected) {
				request.idCompanyType = String(companyTypeSelected.key);
				dispatch(getCompany(request));
			}
		}
	}

	function convertToNullableBoolean(value: number | string | undefined | null): boolean | null {
		if (value == undefined || value == null) return null;
		return value == StatusUserGroupEnum.Active;
	}

	function onSubmit(formInputs: ISearchUserGroupForm) {
		setPageIndex(PaginationEnum.PageIndexIntial);
		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});

		const requestForm: UserGroupFilterRequest = {
			guidCountry: String(formInputs.country?.key),
			idCompanyType: formInputs.companyType?.key ? Number(formInputs.companyType.key) : null,
			guidCompany: formInputs.company?.key ? String(formInputs.company?.key) : null,
			name: formInputs.userGroup,
			active: convertToNullableBoolean(formInputs.statusRegister?.key),
			idSystemApplication: formInputs.systemApplication?.key ? Number(formInputs.systemApplication?.key) : null,
			type: HeadQuarterBranchFilterEnum.All,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
			seeOnlyCompaniesWithAnAgreement: formInputs.companyType?.key == CompanyTypeEnum.Insurer ? false : true,
			guidCompanyInsurer:
				userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer ? userReducer.currentUser?.guidCompany : null,
		};

		setFormFilters(requestForm);
		const requestFormatted = removeEmptyValues(requestForm);
		usergroupApi.getSearchGrid(requestFormatted).then((response) => {
			setGridData({
				isLoading: false,
				totalItems: response.data.totalNumberOfRecords,
				data: response.data.results,
			});
		});
		setShowFilters(!showFilters);
	}

	useEffect(() => {
		// Call API's related by default country
		if (optionDefaultCountryDrop && optionDefaultCompanyTypeDrop) {
			callCompanyApiDomain(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
		}
	}, [optionDefaultCountryDrop?.key, optionDefaultCompanyTypeDrop?.key]);

	// Fill and lock filters for Insurer user
	useEffect(() => {
		if (!isInsurerUser) return;

		const defaultCountry = getDefaultCountryOptionDrop(countryReducer.countriesUser.data);
		if (defaultCountry) {
			setValue("country", defaultCountry);
		}

		const insurerKey = String(CompanyTypeEnum.Insurer);
		const insurerType = companyTypeReducer.companyType.data?.find((x) => String(x.key) === insurerKey) || null;
		if (insurerType) {
			setValue("companyType", insurerType);
			callCompanyApiDomain(defaultCountry, insurerType);
		}
	}, [isInsurerUser, countryReducer.countriesUser.data, companyTypeReducer.companyType.data]);

	useEffect(() => {
		if (!isInsurerUser) return;
		if (companyReducer.company.data && companyReducer.company.data.length > 0) {
			const defaultCompany = getDefaultOptionDrop(companyReducer.company.data);
			if (defaultCompany) {
				setValue("company", defaultCompany);
			}
		}
	}, [isInsurerUser, companyReducer.company.data]);

	useEffect(() => {
		const mountCountryQueryParams = isNullUndefined(queryParamsValues.country) ? null : queryParamsValues.country;
		const mountCompanyTypeQueryParams = isNullUndefined(queryParamsValues.companyType)
			? null
			: queryParamsValues.companyType;

		if (!isNullUndefined(mountCompanyTypeQueryParams))
			callCompanyApiDomain(mountCountryQueryParams, mountCompanyTypeQueryParams);
	}, [JSON.stringify(queryParamsValues)]);

	return (
		<ContainerScreen
			hasBackground
			title={`
      ${getTranslateByKey("information.registration")}
      ${" > "}
      ${getTranslateByKey("information.userGroup")}`}
		>
			<S.Container>
				<AreaButton>
					<Button onClick={handleFilters} id="id-searchUserGroup-button-filters">
						{getTranslateByKey("information.filters")}
					</Button>
					<Button onClick={handleNew} id="id-searchUserGroup-button-newUserGroup">
						{getTranslateByKey("information.newUserGroup")}
					</Button>
				</AreaButton>
				<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
					<form onSubmit={customHandleSubmit} noValidate>
						<S.RowInputs>
							<KogAutoCompleteControlled
								required
								id="id-searchUserGroup-select-country"
								label={getTranslateByKey("information.country")}
								control={control}
								options={countryReducer.countriesUser.data}
								isLoading={countryReducer.countriesUser.isLoading}
								nameCustom="country"
								rulesCustom={{ required: true }}
								errorMessageCustom={
									errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
								}
								hasErrorCustom={!!errors.country}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									const companyTypeSelected = getValues().companyType;
									if (companyTypeSelected) {
										callCompanyApiDomain(optionSelected, companyTypeSelected);
									}
								}}
								valueDefaultKey={optionDefaultCountryDrop?.key}
								setValueCustom={setValue}
								disabled={isInsurerUser || optionDefaultCountryDrop !== null}
							/>
							<KogAutoCompleteControlled
								required
								id="id-searchUserGroup-select-companyType"
								label={getTranslateByKey("information.companyType")}
								control={control}
								options={companyTypeReducer.companyType.data}
								isLoading={companyTypeReducer.companyType.isLoading}
								nameCustom="companyType"
								rulesCustom={{ required: true }}
								errorMessageCustom={
									errors.companyType?.type === "required"
										? getTranslateByKey("information.companyTypeRequired", true)
										: ""
								}
								setValueCustom={setValue}
								hasErrorCustom={!!errors.companyType}
								isDefaultValueUnique
								disabled={isDisabledCompanyTypeDrop}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									callCompanyApiDomain(getValues().country, optionSelected);
								}}
							/>
							<KogAutoCompleteControlled
								id="id-searchUserGroup-select-company"
								label={getTranslateByKey("information.company")}
								control={control}
								nameCustom="company"
								rulesCustom={{ required: false }}
								errorMessageCustom=""
								hasErrorCustom={false}
								options={companyReducer.company.data}
								isLoading={companyReducer.company.isLoading}
								onChangeCustom={() => {}}
								disabled={isDisabledCompanyDrop()}
								valueDefaultKey={
									isValueQueryParams(queryParamsValues.company)
										? queryParamsValues.company.key
										: optionDefaultCompanyDrop?.key
								}
								setValueCustom={setValue}
							/>
						</S.RowInputs>
						<S.RowInputs>
							<KogAutoCompleteControlled
								id="id-searchUserGroup-select-system"
								label={getTranslateByKey("information.systemApplication")}
								control={control}
								nameCustom="systemApplication"
								rulesCustom={{ required: true }}
								errorMessageCustom=""
								hasErrorCustom={false}
								options={systemApplicationReducer.systemApplication.data}
								isLoading={systemApplicationReducer.systemApplication.isLoading}
								onChangeCustom={() => {}}
								disabled={false}
								setValueCustom={setValue}
							/>
							<KogInputTextControlledNew
								name="userGroup"
								id="id-searchUserGroup-inputText-userGroup"
								label={getTranslateByKey("information.userGroup")}
								control={control}
							/>
							<KogAutoCompleteControlled
								id="id-searchUserGroup-select-statusRegister"
								label={getTranslateByKey("information.status")}
								nameCustom="statusRegister"
								errorMessageCustom=""
								control={control}
								setValueCustom={setValue}
								options={statusGroupRegister}
								rulesCustom={{ required: false }}
								valueDefaultKey={statusGroupRegister[0].key}
								hasErrorCustom={false}
								onChangeCustom={() => {}}
							/>
						</S.RowInputs>
						<KOGAreaButtons>
							<SecondaryButton id="id-searchUserGroup-button-submit" type="submit">
								{getTranslateByKey("information.search")}
							</SecondaryButton>
							<SecondaryButton id="id-searchUserGroup-button-clear" onClick={handleClear}>
								{getTranslateByKey("information.clear")}
							</SecondaryButton>
						</KOGAreaButtons>
					</form>
				</Box>

				<UsergroupSearchGrid formFilters={formFilters} gridData={gridData} setGridData={setGridData} />
			</S.Container>
		</ContainerScreen>
	);
}

export default withQueryparams(UsergroupScreen);
